import React from "react";
import { Num, Seg, Toggle, Section, Field } from "./fields";

const SOURCES = [
  { v: "401k", label: "401(k)" }, { v: "trad_ira", label: "Traditional IRA" }, { v: "roth_ira", label: "Roth IRA" },
  { v: "403b", label: "403(b)" }, { v: "457", label: "457(b)" },
];
const GOALS = [
  { v: "tax_free_income", label: "Tax-free income" }, { v: "lifetime_income", label: "Lifetime income" },
  { v: "legacy", label: "Legacy / death benefit" }, { v: "principal_protection", label: "Principal protection" },
  { v: "growth", label: "Protected growth" },
];
const STATES = [["GA", "Georgia"], ["AL", "Alabama"], ["IA", "Iowa"], ["ME", "Maine"], ["MD", "Maryland"], ["NC", "North Carolina"],
  ["OH", "Ohio"], ["PA", "Pennsylvania"], ["SC", "South Carolina"], ["TX", "Texas"], ["VA", "Virginia"], ["OTHER", "Other state"]];

export const StepStart = ({ c, set, settings }) => {
  const horizon = [65, 70, 80].includes(c.income_start_age) ? c.income_start_age : "custom";
  const pickState = (st) => set({ state: st, state_rate: settings.tax.state_rates[st] ?? 0 });
  return (
    <div className="space-y-10">
      <Section title="Where the money is now">
        <Seg testid="ro-source" label="Source account" value={c.source} onChange={(v) => set({ source: v })} options={SOURCES} />
        <div className="grid sm:grid-cols-2 gap-5">
          <Num testid="ro-amount" label="Rollover amount" prefix="$" step={5000} value={c.amount} onChange={(v) => set({ amount: v })} />
          <Num testid="ro-age" label="Your age now" min={18} max={85} value={c.age} onChange={(v) => set({ age: v })} />
        </div>
        <Toggle testid="ro-direct" label="Direct rollover (trustee-to-trustee)" value={c.direct_rollover} onChange={(v) => set({ direct_rollover: v })}
          hint="Recommended. Money moves straight between institutions, with no withholding and no 60-day deadline." />
        {c.source === "roth_ira" && (
          <div className="grid sm:grid-cols-2 gap-5">
            <Toggle testid="ro-roth5" label="Roth open 5+ years" value={c.roth_5yr} onChange={(v) => set({ roth_5yr: v })} />
            <Num testid="ro-roth-basis" label="Contributions (basis) share" suffix="%" value={c.roth_basis_pct} onChange={(v) => set({ roth_basis_pct: v })}
              hint="Only used if distributions aren't qualified." />
          </div>
        )}
      </Section>

      <Section title="Taxes (simplified)">
        <Field label="State">
          <select data-testid="ro-state" value={c.state} onChange={(e) => pickState(e.target.value)} className="input-exodus">
            {STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Num testid="ro-fed" label="Federal marginal rate" suffix="%" step={1} value={c.federal_rate} onChange={(v) => set({ federal_rate: v })} />
          <Num testid="ro-state-rate" label="State rate (editable)" suffix="%" step={0.25} value={c.state_rate} onChange={(v) => set({ state_rate: v })}
            hint={settings.state_notes?.[c.state]} />
        </div>
      </Section>

      <Section title="Timeline & goal">
        <Seg testid="ro-horizon" label="Income starts at age" value={horizon}
          onChange={(v) => v !== "custom" && set({ income_start_age: v })}
          options={[{ v: 65, label: "65" }, { v: 70, label: "70" }, { v: 80, label: "80" }, { v: "custom", label: "Custom" }]} cols="grid-cols-4" />
        <div className="grid sm:grid-cols-2 gap-5">
          <Num testid="ro-income-start" label="Income start age" value={c.income_start_age} onChange={(v) => set({ income_start_age: v })} />
          <Num testid="ro-plan-to" label="Plan income through age" value={c.plan_to_age} onChange={(v) => set({ plan_to_age: v })} />
        </div>
        <Seg testid="ro-income-mode" label="Desired income" value={c.income_mode} onChange={(v) => set({ income_mode: v })}
          options={[{ v: "maximize", label: "Maximize", sub: "Highest level income the model supports" }, { v: "amount", label: "Set an amount", sub: "After-tax dollars per year" }]} />
        {c.income_mode === "amount" && (
          <Num testid="ro-income-amount" label="Yearly after-tax income" prefix="$" step={1000} value={c.income_amount} onChange={(v) => set({ income_amount: v })} />
        )}
        <Seg testid="ro-goal" label="Main goal" value={c.goal} onChange={(v) => set({ goal: v })} options={GOALS} />
      </Section>
    </div>
  );
};
